import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Download, Grid3X3, List, Sparkles, Users } from 'lucide-react';
import PageTitle from '../../components/shared/PageTitle';
import PromotersStats from '../../components/promoters/PromotersStats';
import PromotersList from './PromotersList';
import PromotersKanban from '../../components/promoters/PromotersKanban';
import PromotersKanbanIkigai from './PromotersKanbanIkigai';
import { Can, useAbility } from '../../contexts/AbilityContext';
import { useAuth } from '../../contexts/AuthContext';
import { useGetPromotersQuery } from '../../store/promoterApi';

const TEAM_MEMBER_ROLES = ['team_sales', 'team_customer_success', 'team_account_manager', 'team_manager', 'team_marketing_manager'];

const Promoters: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<'list' | 'kanban' | 'ikigai'>('ikigai');
  const { user } = useAuth();
  const ability = useAbility();
  const { data: promoters = [], isLoading: promotersLoading } = useGetPromotersQuery();

  const isTeamMember = user?.role && TEAM_MEMBER_ROLES.includes(user.role);
  const showTeamMemberEmptyState = isTeamMember && !ability.can('read', 'Promoter') && !promotersLoading && promoters.length === 0;

  if (showTeamMemberEmptyState) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 p-12">
        <Users className="w-16 h-16 text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('promoters.teamMemberEmpty.title')}</h2>
        <p className="text-gray-600 text-center max-w-md mb-6">
          {t('promoters.teamMemberEmpty.description')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle title={t('promoters.pageTitle')} />
      <PromotersStats />

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('promoters.title')}</h1>
        <div className="flex items-center space-x-3">
          <div className="flex items-center rounded-lg p-1" style={{ backgroundColor: '#E4E7EB' }}>
            <button
              onClick={() => setViewMode('ikigai')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'ikigai'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              <span>{t('promoters.views.promoterPower')}</span>
            </button>

            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4" />
              <span>{t('promoters.views.list')}</span>
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'kanban'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid3X3 className="w-4 h-4" />
              <span>{t('promoters.views.kanban')}</span>
            </button>

          </div>
          <Can I="access" a="CrmImport">
            <button
              onClick={() => navigate('/crm-import')}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              <span>{t('promoters.actions.import')}</span>
            </button>
          </Can>
          <Can I="create" a="Promoter">
            <button
              onClick={() => navigate('/add-promoter')}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>{t('promoters.actions.addPromoter')}</span>
            </button>
          </Can>
        </div>
      </div>

      {viewMode === 'ikigai' && (
        <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl shadow-lg border border-gray-200 p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('promoters.power.guideTitle')}</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
             {t('promoters.power.guideDescription')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="relative border-2 border-blue-200 rounded-xl p-6 bg-gradient-to-br from-blue-50 to-blue-100 hover:shadow-lg transition-all duration-200 transform hover:scale-105">
              <div className="absolute -top-3 -right-3 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                1
              </div>
              <div className="text-3xl mb-3">📊</div>
              <h4 className="font-bold text-blue-900 mb-3 text-lg">{t('promoters.power.level1Title')}</h4>
              <p className="text-blue-800 font-medium">{t('promoters.power.level1Desc')}</p>
              <div className="mt-4 pt-4 border-t border-blue-200">
                <p className="text-xs text-blue-700">{t('promoters.power.level1Hint')}</p>
              </div>
            </div>

            <div className="relative border-2 border-green-200 rounded-xl p-6 bg-gradient-to-br from-green-50 to-green-100 hover:shadow-lg transition-all duration-200 transform hover:scale-105">
              <div className="absolute -top-3 -right-3 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                2
              </div>
              <div className="text-3xl mb-3">🥉</div>
              <h4 className="font-bold text-green-900 mb-3 text-lg">{t('promoters.power.advocateTitle')}</h4>
              <p className="text-green-800 font-medium">{t('promoters.power.advocateDesc')}</p>
              <div className="mt-4 pt-4 border-t border-green-200">
                <p className="text-xs text-green-700">{t('promoters.power.advocateHint')}</p>
              </div>
            </div>

            <div className="relative border-2 border-purple-200 rounded-xl p-6 bg-gradient-to-br from-purple-50 to-purple-100 hover:shadow-lg transition-all duration-200 transform hover:scale-105">
              <div className="absolute -top-3 -right-3 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                3
              </div>
              <div className="text-3xl mb-3">🥈</div>
              <h4 className="font-bold text-purple-900 mb-3 text-lg">{t('promoters.power.championTitle')}</h4>
              <p className="text-purple-800 font-medium">{t('promoters.power.championDesc')}</p>
              <div className="mt-4 pt-4 border-t border-purple-200">
                <p className="text-xs text-purple-700">{t('promoters.power.championHint')}</p>
              </div>
            </div>

            <div className="relative border-2 border-orange-200 rounded-xl p-6 bg-gradient-to-br from-orange-50 to-orange-100 hover:shadow-lg transition-all duration-200 transform hover:scale-105">
              <div className="absolute -top-3 -right-3 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                4
              </div>
              <div className="text-3xl mb-3">🥇</div>
              <h4 className="font-bold text-orange-900 mb-3 text-lg">{t('promoters.power.ambassadorTitle')}</h4>
              <p className="text-orange-800 font-medium">{t('promoters.power.ambassadorDesc')}</p>
              <div className="mt-4 pt-4 border-t border-orange-200">
                <p className="text-xs text-orange-700">{t('promoters.power.ambassadorHint')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">
            {viewMode === 'list' ? t('promoters.sections.listTitle') :
             viewMode === 'kanban' ? t('promoters.sections.kanbanTitle') :
             t('promoters.sections.powerTitle')}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {viewMode === 'list'
              ? t('promoters.sections.listDesc')
              : viewMode === 'kanban'
              ? t('promoters.sections.kanbanDesc')
              : t('promoters.sections.powerDesc')
            }
          </p>
        </div>

        <div className="p-6">
          {viewMode === 'list' ? <PromotersList /> : 
           viewMode === 'kanban' ? <PromotersKanban /> : 
           <PromotersKanbanIkigai />}
        </div>
      </div>
    </div>
  );
};

export default Promoters;
